import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { countEarnedMilestonesFromTrendLessons } from "./StudentBadges";

import { API_BASE_URL } from "../config/apiBase";

const API = API_BASE_URL;

function clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(1, x));
}

export default function XPBar({ studentId, currentWeek = 1, currentLesson = 1 }) {
  const navigate = useNavigate();
  const sid = String(studentId || "").toUpperCase();
  const [xpState, setXpState] = useState({
    level: 1,
    xp: 0,
    xp_into_level: 0,
    xp_needed_for_next: 100,
  });
  const [milestoneState, setMilestoneState] = useState({ earned: 0, total: 6 });
  const [courseCursor, setCourseCursor] = useState({
    week: 1,
    lesson: 1,
    completedLessons: 0,
    totalLessons: 16,
  });
  const [loaded, setLoaded] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [xpPop, setXpPop] = useState(false);
  const [showTrophyBurst, setShowTrophyBurst] = useState(false);
  const [trophyBurstSeq, setTrophyBurstSeq] = useState(0);
  const confettiTimerRef = useRef(null);
  const xpPopTimerRef = useRef(null);
  const trophyBurstTimerRef = useRef(null);
  const prevXpRef = useRef(null);
  const prevBadgeCountRef = useRef(null);
  const badgeCountInitializedRef = useRef(false);

  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 40 }).map((_, i) => ({
        key: i,
        x: Math.floor(Math.random() * 100),
        r: Math.floor(Math.random() * 360),
        d: Math.floor(Math.random() * 240),
        dx: Math.floor(Math.random() * 80) - 40,
        w: 6 + Math.floor(Math.random() * 8),
        h: 8 + Math.floor(Math.random() * 10),
        spin: 720 + Math.floor(Math.random() * 720),
      })),
    []
  );

  const progress = useMemo(() => {
    const total = Number(courseCursor.totalLessons) || 16;
    const completed = Number(courseCursor.completedLessons) || 0;
    if (total <= 0) return 0;
    return clamp01(completed / total);
  }, [courseCursor.completedLessons, courseCursor.totalLessons]);

  const courseLabel = useMemo(() => {
    const week = Math.max(1, Math.min(4, Number(courseCursor.week) || 1));
    const lesson = Math.max(1, Math.min(4, Number(courseCursor.lesson) || 1));
    return `Week ${week}/4 • Lesson ${lesson}/4`;
  }, [courseCursor.lesson, courseCursor.week]);

  const refresh = useCallback(async () => {
    if (!sid) return;
    try {
      const [xpRes, dashboardRes] = await Promise.all([
        fetch(`${API}/api/xp/${sid}`, { cache: "no-store" }),
        fetch(`${API}/student/${sid}/dashboard_v2`, { cache: "no-store" }),
      ]);
      const [xpJson, dashboardJson] = await Promise.all([
        xpRes.json().catch(() => null),
        dashboardRes.json().catch(() => null),
      ]);

      if (xpRes.ok && xpJson) {
        setXpState({
          level: xpJson.level,
          xp: xpJson.xp,
          xp_into_level: xpJson.xp_into_level,
          xp_needed_for_next: xpJson.xp_needed_for_next,
        });
      } else {
        console.error("XPBar refresh failed:", `${API}/api/xp/${sid}`, xpRes.status);
      }

      if (!dashboardRes.ok) {
        console.error("XPBar refresh failed:", `${API}/student/${sid}/dashboard_v2`, dashboardRes.status);
      }

      const currentWeekRaw = Number(currentWeek || dashboardJson?.summary?.current_week || dashboardJson?.current_week || 1);
      const currentLessonRaw = Number(currentLesson || dashboardJson?.summary?.current_lesson || dashboardJson?.current_lesson || 1);
      const currentWeekValue = Math.max(1, Math.min(4, Number.isFinite(currentWeekRaw) ? currentWeekRaw : 1));
      const currentLessonValue = Math.max(1, Math.min(4, Number.isFinite(currentLessonRaw) ? currentLessonRaw : 1));
      const completedLessons = Math.max(
        0,
        Math.min(16, (currentWeekValue - 1) * 4 + (currentLessonValue - 1))
      );
      setCourseCursor({
        week: currentWeekValue,
        lesson: currentLessonValue,
        completedLessons,
        totalLessons: 16,
      });

      setMilestoneState(
        countEarnedMilestonesFromTrendLessons(dashboardJson?.trend?.lessons, {
          currentWeek: currentWeekValue,
          currentLesson: currentLessonValue,
        })
      );
    } catch (e) {
      console.error("XPBar refresh failed:", `${API}/api/xp/${sid}`, e);
    } finally {
      setLoaded(true);
    }
  }, [sid, currentWeek, currentLesson]);

  function triggerConfetti() {
    setShowConfetti(true);
    if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
    confettiTimerRef.current = setTimeout(() => setShowConfetti(false), 2800);
  }

  function triggerXpPop() {
    setXpPop(true);
    if (xpPopTimerRef.current) clearTimeout(xpPopTimerRef.current);
    xpPopTimerRef.current = setTimeout(() => setXpPop(false), 220);
  }

  function triggerTrophyBurst() {
    setTrophyBurstSeq((value) => value + 1);
    setShowTrophyBurst(true);
    if (trophyBurstTimerRef.current) clearTimeout(trophyBurstTimerRef.current);
    trophyBurstTimerRef.current = setTimeout(() => setShowTrophyBurst(false), 1100);
  }

  useEffect(() => {
    refresh();
    return () => {
      if (confettiTimerRef.current) clearTimeout(confettiTimerRef.current);
      if (xpPopTimerRef.current) clearTimeout(xpPopTimerRef.current);
      if (trophyBurstTimerRef.current) clearTimeout(trophyBurstTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, sid]);

  useEffect(() => {
    const currentXp = Number(xpState?.xp);
    if (!loaded || !Number.isFinite(currentXp)) return;

    if (prevXpRef.current == null) {
      prevXpRef.current = currentXp;
      return;
    }

    if (currentXp > prevXpRef.current) {
      prevXpRef.current = currentXp;
      triggerXpPop();
    } else {
      prevXpRef.current = currentXp;
    }
  }, [xpState?.xp, loaded]);

  useEffect(() => {
    const currentMilestones = Number(milestoneState?.earned);
    if (!loaded || !Number.isFinite(currentMilestones)) return;

    if (!badgeCountInitializedRef.current) {
      prevBadgeCountRef.current = currentMilestones;
      badgeCountInitializedRef.current = true;
      return;
    }

    const previousMilestones = Number(prevBadgeCountRef.current);
    prevBadgeCountRef.current = currentMilestones;

    if (Number.isFinite(previousMilestones) && currentMilestones > previousMilestones) {
      triggerTrophyBurst();
    }
  }, [milestoneState?.earned, loaded]);

  useEffect(() => {
    function onXpUpdated(e) {
      const detail = e?.detail || {};
      const eventSid = String(detail.studentId || "").toUpperCase();
      if (!eventSid || eventSid !== sid) return;

      if (detail.xpState) {
        setXpState(detail.xpState);
        setLoaded(true);
      }
      refresh();

      if (detail.leveledUp && Number(detail.newLevel) === 2) {
        triggerConfetti();
      }
    }

    window.addEventListener("studentXpUpdated", onXpUpdated);
    return () => window.removeEventListener("studentXpUpdated", onXpUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  return (
    <div className="xp-row" aria-label="Experience">
      {showConfetti && (
        <div className="xp-confetti" aria-hidden>
          {confettiPieces.map((p) => (
            <span
              key={p.key}
              className="xp-confetti-piece"
              style={{ "--x": p.x, "--r": p.r, "--d": p.d, "--dx": p.dx, "--w": p.w, "--h": p.h, "--spin": p.spin }}
            />
          ))}
        </div>
      )}

      <div className={`xp-wrap ${xpPop ? "is-pop" : ""}`.trim()}>
        <div className="xp-meta">
          <div className="xp-course-label">{loaded ? courseLabel : "Loading…"}</div>
          <div
            className="xp-course-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            aria-label={`Course progress: ${Math.round(progress * 100)}%`}
          >
            <div className="xp-course-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
          </div>
        </div>
      </div>

      <div
        className="xp-milestones"
        aria-label={`Badges earned: ${milestoneState.earned} of ${milestoneState.total}`}
      >
        <button
          type="button"
          className={`xp-milestones-btn ${showTrophyBurst ? "is-burst" : ""}`.trim()}
          onClick={() => navigate(`/student/dashboard/${sid}/badges`)}
          aria-label={`Badges earned: ${milestoneState.earned} of ${milestoneState.total}`}
          title={`Badges earned: ${milestoneState.earned} of ${milestoneState.total}`}
          onAnimationEnd={(e) => {
            if (e.target !== e.currentTarget) return;
            setShowTrophyBurst(false);
          }}
        >
          {showTrophyBurst ? (
            <span key={trophyBurstSeq} className="xp-milestones-burst" aria-hidden="true">
              🏆
            </span>
          ) : null}
          <span className="xp-milestones-icon" aria-hidden="true">🏆</span>
          <span className="xp-milestones-count">
            Badges {milestoneState.earned}/{milestoneState.total}
          </span>
        </button>
      </div>

    </div>
  );
}
